import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Calendar,
  Clock,
  MapPin,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Send,
  Home,
  Plane,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OpponentBookingPanel({ hostOrgId, hostOrgName, requestingOrgId, requestingTeamId, requestingTeamName }) {
  const { isDark } = useTheme();
  const [availableData, setAvailableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isAway, setIsAway] = useState(true);
  const [message, setMessage] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [booked, setBooked] = useState(false);

  const bg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  const fetchAvailability = useCallback(async () => {
    if (!hostOrgId) return;
    try {
      const res = await axios.get(`${API_URL}/api/scheduler/available-slots/${hostOrgId}`, {
        params: { team_id: requestingTeamId },
        withCredentials: true,
      });
      setAvailableData(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [hostOrgId, requestingTeamId]);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  };

  const formatDateStr = (d) => {
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const availableDatesMap = {};
  (availableData?.available_dates || []).forEach(d => { availableDatesMap[d.date] = d; });

  const handleBookSlot = async () => {
    if (!selectedDate || !selectedSlot) return;
    setBooking(true);
    try {
      await axios.post(`${API_URL}/api/scheduler/request-game`, {
        host_org_id: hostOrgId,
        host_team_id: '',
        host_venue_id: availableData?.venues?.[0]?.id || '',
        requesting_team_id: requestingTeamId,
        requesting_org_id: requestingOrgId,
        date: selectedDate,
        start_time: selectedSlot.start_time,
        duration_minutes: selectedSlot.duration_minutes,
        is_away: isAway,
        message,
      }, { withCredentials: true });
      setShowConfirm(false);
      setBooked(true);
      fetchAvailability();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to request game';
      alert(detail);
    }
    setBooking(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  if (booked) {
    return (
      <Card className={bg}>
        <CardContent className="py-12 text-center">
          <div className="text-green-500 text-4xl mb-3">&#10003;</div>
          <h3 className={`text-lg font-semibold ${text}`}>Game Request Sent!</h3>
          <p className={`text-sm ${muted} mt-2`}>Your request has been submitted. The scheduling manager will accept or reject it.</p>
          <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { setBooked(false); setSelectedDate(null); setSelectedSlot(null); }}>
            Book Another Game
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!availableData || availableData.available_dates?.length === 0) {
    return (
      <Card className={bg}>
        <CardContent className="py-12 text-center">
          <Calendar className={`h-10 w-10 mx-auto mb-3 ${muted}`} />
          <p className={`text-sm ${muted}`}>{availableData?.message || 'No available time slots. The scheduling manager may not have configured their availability yet.'}</p>
        </CardContent>
      </Card>
    );
  }

  const days = getDaysInMonth(calendarMonth);
  const monthStr = calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const dateData = selectedDate ? availableDatesMap[selectedDate] : null;

  return (
    <div className="space-y-4" data-testid="opponent-booking-panel">
      <div className="flex items-center gap-2 mb-2">
        <Calendar className="h-5 w-5 text-blue-500" />
        <h2 className={`text-lg font-semibold ${text}`}>Book a Game with {hostOrgName || 'Opponent'}</h2>
      </div>

      {/* Info bar */}
      <div className={`flex items-center gap-4 p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
        <span className={`text-xs ${muted}`}>Season: {availableData.season_start} to {availableData.season_end}</span>
        <span className={`text-xs ${muted}`}>Game Duration: {availableData.game_duration} min</span>
        <span className={`text-xs ${muted}`}>Select an available date and time below</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Calendar */}
        <Card className={bg}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-4">
              <Button size="sm" variant="ghost" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className={`text-sm font-semibold ${text}`}>{monthStr}</span>
              <Button size="sm" variant="ghost" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                <div key={d} className={`text-center text-xs font-medium ${muted}`}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="booking-calendar">
              {days.map((day, idx) => {
                if (!day) return <div key={`e-${idx}`} className="h-10" />;
                const dateStr = formatDateStr(day);
                const hasSlots = !!availableDatesMap[dateStr];
                const isSelected = selectedDate === dateStr;
                return (
                  <button key={dateStr}
                    className={`h-10 rounded-lg text-sm font-medium transition-colors
                      ${hasSlots ? (isSelected ? 'bg-blue-600 text-white' : isDark ? 'bg-slate-700 text-white hover:bg-blue-800' : 'bg-blue-50 text-blue-700 hover:bg-blue-100') : (isDark ? 'text-slate-600' : 'text-slate-300')}
                    `}
                    disabled={!hasSlots}
                    onClick={() => { setSelectedDate(dateStr); setSelectedSlot(null); }}
                    data-testid={`book-day-${dateStr}`}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Time slots */}
        <Card className={bg}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm font-medium ${muted}`}>
              {selectedDate ? `Available Times — ${selectedDate}` : 'Select a date to view available times'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!dateData ? (
              <p className={`text-sm text-center py-8 ${muted}`}><Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />Pick a highlighted date on the calendar</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-80 overflow-y-auto" data-testid="time-slots">
                {dateData.slots.map((slot, idx) => (
                  <button key={idx}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors
                      ${selectedSlot === slot ? 'bg-blue-600 text-white border-blue-600' : isDark ? 'border-slate-600 text-slate-300 hover:border-blue-500' : 'border-slate-200 text-slate-700 hover:border-blue-400'}
                    `}
                    onClick={() => { setSelectedSlot(slot); setShowConfirm(true); }}
                    data-testid={`slot-${slot.start_time}`}
                  >
                    {slot.start_time}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm Booking Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={text}>Confirm Game Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="confirm-booking-dialog">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
              <p className={`text-sm font-medium ${text}`}>{selectedDate} at {selectedSlot?.start_time}</p>
              <p className={`text-xs ${muted}`}>Duration: {selectedSlot?.duration_minutes} minutes</p>
              <p className={`text-xs ${muted}`}>Team: {requestingTeamName || 'Your team'} vs {hostOrgName || 'Host'}</p>
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Home or Away?</Label>
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant={!isAway ? 'default' : 'outline'}
                  className={!isAway ? 'bg-blue-600' : isDark ? 'border-slate-600 text-slate-300' : ''}
                  onClick={() => setIsAway(false)} data-testid="home-btn">
                  <Home className="h-3 w-3 mr-1" /> Home
                </Button>
                <Button size="sm" variant={isAway ? 'default' : 'outline'}
                  className={isAway ? 'bg-blue-600' : isDark ? 'border-slate-600 text-slate-300' : ''}
                  onClick={() => setIsAway(true)} data-testid="away-btn">
                  <Plane className="h-3 w-3 mr-1" /> Away
                </Button>
              </div>
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Message (optional)</Label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                className={`w-full h-16 rounded-md px-3 py-2 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                placeholder="Any notes for the scheduling manager..."
                data-testid="booking-message" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowConfirm(false)} className={muted}>Cancel</Button>
              <Button onClick={handleBookSlot} disabled={booking}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-booking-btn">
                {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Request Game</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
